import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { ArticleCard, timeAgo } from "@/components/ArticleCards";
import { useLang, localizeArticle } from "@/context/LanguageContext";
import AdBanner from "@/components/AdBanner";
import Countdown from "@/components/Countdown";
import { ArrowLeft, User, Clock, Calendar, Share2, Facebook, ExternalLink, Instagram, X, ChevronLeft, ChevronRight, MessageCircle, Send, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";

function renderBody(body) {
  const blocks = body.split("\n\n");
  return blocks.map((b, i) => {
    if (b.startsWith("## ")) return <h2 key={i}>{b.replace("## ", "")}</h2>;
    if (b.startsWith("- ")) {
      const items = b.split("\n").filter((l) => l.startsWith("- "));
      return (
        <ul key={i} className="mb-5 space-y-2">
          {items.map((it, j) => <li key={j} className="flex gap-2 text-[1.05rem] leading-8 text-slate-700 dark:text-slate-300"><span className="text-legend-red font-bold">▸</span><span dangerouslySetInnerHTML={{ __html: mdInline(it.replace("- ", "")) }} /></li>)}
        </ul>
      );
    }
    return <p key={i} dangerouslySetInnerHTML={{ __html: mdInline(b) }} />;
  });
}

function mdInline(t) {
  const html = t
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-legend-red underline font-semibold">$1</a>');
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "a", "em", "br"], ALLOWED_ATTR: ["href", "target", "rel", "class"] });
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i === null ? i : (i + 1) % (data?.article?.gallery?.length || 1)));
      if (e.key === "ArrowLeft") setLightbox((i) => (i === null ? i : (i - 1 + (data?.article?.gallery?.length || 1)) % (data?.article?.gallery?.length || 1)));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, data]);

  useEffect(() => {
    window.scrollTo(0, 0);
    client.get(`/articles/${slug}`).then((r) => setData(r.data)).catch(() => navigate("/"));
  }, [slug, navigate]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const p = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      setProgress(p);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!data) return <div className="min-h-[60vh] grid place-items-center text-slate-400 font-mono">{t("loading")}</div>;
  const { related } = data;
  const a = localizeArticle(data.article, lang);

  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${slug}`;
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: a.title, url: shareUrl }); } catch { /* condivisione annullata dall'utente */ } }
    else { await navigator.clipboard.writeText(shareUrl); toast.success("Link copiato negli appunti!"); }
  };

  return (
    <article data-testid="article-detail-page">
      <div className="fixed top-0 left-0 h-1 bg-legend-red z-[60] transition-all" style={{ width: `${progress}%` }} />

      {/* Hero */}
      <div className="relative bg-legend-ink text-white">
        {a.cover_image && <img src={a.cover_image} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-45" />}
        <div className="absolute inset-0 bg-gradient-to-t from-legend-ink via-legend-ink/70 to-legend-ink/30" />
        <div className="relative max-w-4xl mx-auto px-4 pt-10 pb-14">
          <button onClick={() => navigate(-1)} data-testid="article-back-btn" className="flex items-center gap-2 text-sm font-mono uppercase text-slate-300 hover:text-legend-yellow mb-6">
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <span className="inline-block bg-legend-red text-white text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-sm mb-4">{a.category}</span>
          <h1 className="font-display font-black uppercase text-4xl md:text-6xl leading-[0.95] mb-4">{a.title}</h1>
          <p className="text-lg text-slate-200 max-w-3xl font-serif italic">{a.subtitle}</p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs font-mono text-slate-300">
            <span className="flex items-center gap-1"><User size={13} /> {a.author}</span>
            <span className="flex items-center gap-1"><Calendar size={13} /> {new Date(a.published_at).toLocaleDateString(t("dateLocale"), { day: "2-digit", month: "long", year: "numeric" })}</span>
            <span className="flex items-center gap-1"><Clock size={13} /> {a.read_time} {t("readTime")}</span>
            {a.issue && <span className="bg-white/10 px-2 py-0.5 rounded-sm">{t("issueLabel")} {a.issue}</span>}
          </div>
        </div>
      </div>

      {["coppa-romagna-slalom-sogliano-17-18-ottobre-2026", "coppa-romagna-classifica-legend-team-in-pista"].includes(slug) && (
        <div className="max-w-4xl mx-auto px-4 pt-6" data-testid="article-mensilita-notice">
          <div className="flex items-center gap-2 rounded-sm border border-legend-yellow bg-legend-yellow/10 px-4 py-3">
            <CalendarClock size={18} className="text-legend-red shrink-0" />
            <p className="text-sm font-semibold">Nei prossimi giorni la mensilità verrà aggiornata con i nuovi contenuti.</p>
          </div>
        </div>
      )}

      {slug === "coppa-romagna-slalom-sogliano-17-18-ottobre-2026" && (
        <div data-testid="article-countdown"><Countdown /></div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-10 grid lg:grid-cols-[1fr_auto] gap-8">
        <div>
          <div className="article-body">{renderBody(a.body)}</div>

          {a.video_url && (
            <div className="my-8" data-testid="article-video">
              {a.video_url.includes("instagram.com") ? (
                <iframe className="w-full max-w-[420px] h-[720px] mx-auto block rounded-sm border border-slate-200 bg-white" src={a.video_url} title={a.title} loading="lazy" scrolling="no" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowFullScreen data-testid="article-instagram-embed" />
              ) : (
                <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                  <iframe className="absolute inset-0 w-full h-full rounded-sm" src={a.video_url} title={a.title} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              )}
            </div>
          )}

          {a.gallery?.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4 my-8">
              {a.gallery.map((g, i) => (
                <figure key={g.url || i}>
                  <button
                    type="button"
                    onClick={() => setLightbox(i)}
                    data-testid={`gallery-image-${i}`}
                    className="group relative block w-full overflow-hidden rounded-sm cursor-zoom-in"
                    aria-label="Ingrandisci foto"
                  >
                    <img src={g.url} alt={g.caption} className="rounded-sm w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute inset-0 bg-legend-ink/0 group-hover:bg-legend-ink/25 transition-colors" />
                    <span className="absolute bottom-2 right-2 grid place-items-center w-8 h-8 rounded-full bg-legend-ink/70 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight size={16} className="rotate-45" />
                    </span>
                  </button>
                  {g.caption && <figcaption className="text-xs text-slate-500 font-mono mt-1">{g.caption}</figcaption>}
                </figure>
              ))}
            </div>
          )}

          {a.instagram_url && (
            <div className="my-8" data-testid="article-instagram">
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">{t("videoOnIg")}</p>
              <a href={a.instagram_url} target="_blank" rel="noreferrer" className="group flex items-center gap-4 rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper p-5 hover:border-legend-red transition-colors">
                <span className="grid place-items-center w-14 h-14 rounded-sm bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white shrink-0">
                  <Instagram size={26} />
                </span>
                <div className="flex-1">
                  <p className="font-display font-bold uppercase leading-none group-hover:text-legend-red transition-colors">{t("watchInterview")}</p>
                  <p className="text-sm text-slate-500 mt-1">{t("igReel")}</p>
                </div>
                <ExternalLink size={18} className="text-slate-400 group-hover:text-legend-red" />
              </a>
            </div>
          )}

          {a.sponsor && (
            <div className="my-8">
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">{t("withSupport")}</p>
              {a.sponsor.logo
                ? <AdBanner variant="sponsor" shape="leaderboard" sponsor={a.sponsor} />
                : <AdBanner variant="advertise" shape="leaderboard" />}
            </div>
          )}
        </div>

        {/* Share sidebar */}
        <aside className="lg:sticky lg:top-20 h-fit flex lg:flex-col gap-3">
          <button onClick={share} data-testid="article-share-btn" className="grid place-items-center w-11 h-11 rounded-sm border border-slate-300 dark:border-slate-700 hover:bg-legend-red hover:text-white hover:border-legend-red transition-colors" aria-label="Condividi">
            <Share2 size={18} />
          </button>
          <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(a.title + " — " + shareUrl)}`} target="_blank" rel="noreferrer" data-testid="article-share-whatsapp" className="grid place-items-center w-11 h-11 rounded-sm border border-slate-300 dark:border-slate-700 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors" aria-label="Condividi su WhatsApp">
            <MessageCircle size={18} />
          </a>
          <a href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(a.title)}`} target="_blank" rel="noreferrer" data-testid="article-share-telegram" className="grid place-items-center w-11 h-11 rounded-sm border border-slate-300 dark:border-slate-700 hover:bg-[#0088cc] hover:text-white hover:border-[#0088cc] transition-colors" aria-label="Condividi su Telegram">
            <Send size={18} />
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="article-share-facebook" className="grid place-items-center w-11 h-11 rounded-sm border border-slate-300 dark:border-slate-700 hover:bg-legend-red hover:text-white hover:border-legend-red transition-colors" aria-label="Condividi su Facebook">
            <Facebook size={18} />
          </a>
        </aside>
      </div>

      {/* Related */}
      {related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-6 w-1.5 bg-legend-red" />
            <h2 className="font-display font-black uppercase text-2xl tracking-wide">{t("continueReading")}</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((r) => <ArticleCard key={r.slug} article={r} />)}
          </div>
        </section>
      )}

      {lightbox !== null && a.gallery?.[lightbox] && (
        <div
          className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
        >
          <button onClick={() => setLightbox(null)} data-testid="lightbox-close" className="absolute top-4 right-4 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
            <X size={22} />
          </button>
          {a.gallery.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + a.gallery.length) % a.gallery.length); }}
                data-testid="lightbox-prev"
                className="absolute left-3 md:left-6 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              ><ChevronLeft size={24} /></button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % a.gallery.length); }}
                data-testid="lightbox-next"
                className="absolute right-3 md:right-6 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              ><ChevronRight size={24} /></button>
            </>
          )}
          <figure className="max-w-5xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={a.gallery[lightbox].url} alt={a.gallery[lightbox].caption} className="max-h-[80vh] w-auto max-w-full object-contain rounded-sm" data-testid="lightbox-image" />
            {a.gallery[lightbox].caption && (
              <figcaption className="text-center text-sm text-white/80 font-mono mt-4 max-w-2xl">{a.gallery[lightbox].caption}</figcaption>
            )}
            {a.gallery.length > 1 && (
              <span className="text-xs text-white/50 font-mono mt-2">{lightbox + 1} / {a.gallery.length}</span>
            )}
          </figure>
        </div>
      )}
    </article>
  );
}
